/**
 * Site Video Management Service
 * Handles site-wide promotional video uploads and management (homepage, sales pages)
 */
import axios, { AxiosInstance, AxiosProgressEvent } from 'axios';
import { getConfig } from '../config/Constants';

// ============================================================================
// Types
// ============================================================================

export interface SiteVideoListItem {
  id: string;
  page_key: string;
  page_name: string;
  video_url?: string;
  s3_key?: string;
  file_size?: number;
  duration?: number;
  uploaded_by?: string;
  uploaded_at?: string;
  updated_at: string;
  is_active: boolean;
}

export interface SiteVideoListResponse {
  videos: SiteVideoListItem[];
  total_count: number;
}

export interface SiteVideoUploadData {
  pageKey: string;
  pageName: string;
  file: File;
  duration?: number;
}

export interface VideoUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface SiteVideoGenerateUploadUrlResponse {
  upload_url: string;
  s3_key: string;
  page_key: string;
  expires_in_seconds: number;
}

export interface SiteVideoConfirmUploadResponse {
  success: boolean;
  message: string;
  video_url: string;
  size_mb: number;
  page_key: string;
}

export interface SiteVideoDeleteResponse {
  success: boolean;
  message: string;
  page_key: string;
}

export interface SiteVideoGetResponse {
  page_key: string;
  page_name: string;
  video_url?: string;
  playback_url?: string;
  file_size?: number;
  duration?: number;
  is_active: boolean;
}

export interface SiteVideoCheckExistsResponse {
  exists: boolean;
  page_key: string;
  file_size?: number;
  duration?: number;
  s3_key?: string;
}

// ============================================================================
// Safe localStorage wrappers for iOS compatibility
// ============================================================================

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`[SiteVideoService] localStorage.getItem('${key}') failed:`, error);
    return null;
  }
}

function safeLocalStorageRemove(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[SiteVideoService] localStorage.removeItem('${key}') failed:`, error);
    return false;
  }
}

// ============================================================================
// Axios Instance with Interceptors
// ============================================================================

let apiInstance: AxiosInstance | null = null;

function getApiInstance(): AxiosInstance {
  if (!apiInstance) {
    const config = getConfig();

    apiInstance = axios.create({
      baseURL: config.apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to add auth token
    apiInstance.interceptors.request.use(
      (config) => {
        // Look for token in localStorage (with safe access for iOS)
        const token = safeLocalStorageGet('access_token') ||
                     safeLocalStorageGet('token') ||
                     safeLocalStorageGet('accessToken') ||
                     safeLocalStorageGet('authToken');

        if (token && token !== 'undefined' && token !== 'null') {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        console.error('[SiteVideoService] Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle errors
    apiInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          safeLocalStorageRemove('access_token');
          safeLocalStorageRemove('user');
          safeLocalStorageRemove('token');
          window.location.href = '/sign-in';
        }
        return Promise.reject(error);
      }
    );
  }

  return apiInstance;
}

// ============================================================================
// Service Class
// ============================================================================

class SiteVideoService {
  /**
   * Get all site videos (admin only)
   */
  async getAllSiteVideos(): Promise<SiteVideoListResponse> {
    try {
      const response = await getApiInstance().get<SiteVideoListResponse>(
        '/admin/site-videos/list'
      );
      return response.data;
    } catch (error: any) {
      console.error('[SiteVideoService] Error listing site videos:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to fetch site videos');
    }
  }

  /**
   * Get a specific site video by page key (public endpoint)
   */
  async getSiteVideo(pageKey: string): Promise<SiteVideoGetResponse> {
    try {
      const response = await axios.get<SiteVideoGetResponse>(
        `${getConfig().apiUrl}/site-videos/${pageKey}`
      );
      return response.data;
    } catch (error: any) {
      console.error('[SiteVideoService] Error getting site video:', error);
      if (error.response?.status === 404) {
        throw new Error('Video not found');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to get site video');
    }
  }

  /**
   * Check if a video exists for a page (public endpoint)
   */
  async checkVideoExists(pageKey: string): Promise<SiteVideoCheckExistsResponse> {
    try {
      const response = await axios.get<SiteVideoCheckExistsResponse>(
        `${getConfig().apiUrl}/site-videos/${pageKey}/check`
      );
      return response.data;
    } catch (error: any) {
      console.error('[SiteVideoService] Error checking video existence:', error);
      return { exists: false, page_key: pageKey };
    }
  }

  /**
   * Generate presigned URL for upload (admin only)
   */
  async generateUploadUrl(
    pageKey: string,
    pageName: string
  ): Promise<SiteVideoGenerateUploadUrlResponse> {
    try {
      const response = await getApiInstance().post<SiteVideoGenerateUploadUrlResponse>(
        '/admin/site-videos/generate-upload-url',
        {
          page_key: pageKey,
          page_name: pageName
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('[SiteVideoService] Error generating upload URL:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to generate upload URL');
    }
  }

  /**
   * Upload directly to S3 using presigned URL
   */
  async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: VideoUploadProgress) => void
  ): Promise<void> {
    const maxRetries = 3;
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type || 'video/mp4',
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onProgress && progressEvent.total) {
              const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress({
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                percentage,
              });
            }
          },
          timeout: 0, // No timeout for large files
        });
        return;

      } catch (error: any) {
        lastError = error;
        console.error(`[SiteVideoService] S3 upload attempt ${attempt} failed:`, error);

        if (error.response?.status === 403 || error.response?.status === 404) {
          throw new Error('Upload URL expired or invalid. Please try again.');
        }

        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    console.error('[SiteVideoService] All S3 upload attempts failed');
    throw new Error(
      lastError?.message ||
      'Failed to upload video after multiple attempts. Please check your connection and try again.'
    );
  }

  /**
   * Confirm upload with backend (admin only)
   */
  async confirmUpload(
    pageKey: string,
    pageName: string,
    s3Key: string,
    fileSize: number,
    duration?: number
  ): Promise<SiteVideoConfirmUploadResponse> {
    try {
      const response = await getApiInstance().post<SiteVideoConfirmUploadResponse>(
        '/admin/site-videos/confirm-upload',
        {
          page_key: pageKey,
          page_name: pageName,
          s3_key: s3Key,
          file_size: fileSize,
          duration: duration
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('[SiteVideoService] Error confirming upload:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to confirm upload');
    }
  }

  /**
   * Upload video using presigned URL flow (full process)
   */
  async uploadVideoPresigned(
    data: SiteVideoUploadData,
    onProgress?: (progress: VideoUploadProgress) => void,
    onStatusChange?: (status: string) => void
  ): Promise<SiteVideoConfirmUploadResponse> {
    try {
      // Step 1: Request presigned URL
      onStatusChange?.('Requesting upload URL...');
      const urlResponse = await this.generateUploadUrl(
        data.pageKey,
        data.pageName
      );

      if (!urlResponse.upload_url || !urlResponse.s3_key) {
        throw new Error('Invalid presigned URL response');
      }
      onStatusChange?.('Uploading to cloud storage...');

      // Step 2: Upload directly to S3
      await this.uploadToS3(
        urlResponse.upload_url,
        data.file,
        onProgress
      );
      onStatusChange?.('Verifying upload...');

      // Step 3: Confirm upload with backend
      const confirmResponse = await this.confirmUpload(
        data.pageKey,
        data.pageName,
        urlResponse.s3_key,
        data.file.size,
        data.duration
      );
      onStatusChange?.('Upload complete!');

      return confirmResponse;

    } catch (error: any) {
      console.error('[SiteVideoService] Presigned upload error:', error);

      if (error.message.includes('presigned URL')) {
        throw new Error('Failed to get upload URL. Please try again.');
      }
      if (error.message.includes('S3') || error.message.includes('storage')) {
        throw new Error('Failed to upload to storage. Please check your connection and try again.');
      }
      if (error.message.includes('confirm')) {
        throw new Error('Upload succeeded but confirmation failed. Please contact support.');
      }

      throw error;
    }
  }

  /**
   * Delete a site video (admin only)
   */
  async deleteSiteVideo(pageKey: string): Promise<SiteVideoDeleteResponse> {
    try {
      const response = await getApiInstance().delete<SiteVideoDeleteResponse>(
        `/admin/site-videos/${pageKey}`
      );
      return response.data;
    } catch (error: any) {
      console.error('[SiteVideoService] Error deleting site video:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to delete site video');
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Validate video file
   */
  validateVideoFile(file: File): { valid: boolean; error?: string } {
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: MP4, WebM, MOV, AVI`,
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File too large. Maximum size is 2GB (your file: ${this.formatFileSize(file.size)})`,
      };
    }

    return { valid: true };
  }

  /**
   * Format duration from seconds to MM:SS format
   */
  formatDuration(seconds?: number): string {
    if (!seconds) return '--:--';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
}

// ============================================================================
// Export Singleton
// ============================================================================

export const siteVideoService = new SiteVideoService();
